
import { Variant, StockMovement, MovementType, StockView } from '../types';

/**
 * Service to interact with Cloudflare Pages Functions API.
 * This replaces the localStorage mock.
 */

// Helper to handle API errors
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as any).error || `API Error: ${response.statusText}`);
  }
  return response.json();
}

// --- API Methods ---

export const fetchStock = async (query: string = ''): Promise<StockView[]> => {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  
  const data = await handleResponse<any[]>(await fetch(`/api/stock?${params.toString()}`));
  
  // Map DB View result (snake_case) to Frontend Interface (camelCase where needed)
  return data.map(row => ({
    id: row.variant_id, // Map View's variant_id to Variant.id
    product_id: row.product_id,
    sku: row.sku,
    color: row.color,
    size: row.size,
    active: row.variant_active !== undefined ? Boolean(row.variant_active) : true,
    sort_order: row.variant_sort_order ?? 0,
    productName: row.product_name,
    productDescription: row.product_description ?? null,
    productActive: row.product_active !== undefined ? Boolean(row.product_active) : true,
    productSortOrder: row.product_sort_order ?? 0,
    productDisplayColor: row.product_display_color ?? null,
    productIconUrl: row.product_icon_url ?? null,
    base_sku: row.base_sku,
    current_stock: row.current_stock
  }));
};

export const fetchVariantBySku = async (sku: string): Promise<Variant | null> => {
  try {
    const data = await handleResponse<any>(await fetch(`/api/variant?sku=${encodeURIComponent(sku)}`));
    if (!data) return null;
    
    return {
      id: data.id,
      product_id: data.product_id,
      sku: data.sku,
      color: data.color,
      size: data.size,
      active: !!data.active,
      productName: data.product_name // from JOIN
    };
  } catch (e) {
    // 404 is technically an error in handleResponse, but for 'fetch' logic we return null
    return null;
  }
};

export const quickCreateVariant = async (sku: string): Promise<Variant> => {
  const result = await handleResponse<{ success: boolean, data: Variant }>(
    await fetch('/api/quick-create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sku })
    })
  );
  return result.data;
};

export const bulkCreateProducts = async (payload: {
  name: string;
  model: string;
  colors: string[];
  sizes: string[];
  separator?: string;
}): Promise<{ success: boolean; productId: number; variantsCount: number }> => {
  return handleResponse(
    await fetch('/api/bulk-create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );
};

export const saveMovementBatch = async (
  type: MovementType,
  items: { variant_id: number; quantity: number }[],
  reason?: string
): Promise<boolean> => {
  await handleResponse(await fetch('/api/movements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, items, reason })
  }));
  return true;
};

export const fetchMovementsHistory = async (): Promise<(StockMovement & { sku: string; productName: string })[]> => {
  const data = await handleResponse<any[]>(await fetch('/api/movements'));

  return data.map(row => ({
    id: row.id,
    variant_id: row.variant_id,
    type: row.type as MovementType,
    quantity: row.quantity,
    reason: row.reason,
    batch_id: row.batch_id,
    created_at: row.created_at,
    sku: row.sku,
    productName: row.product_name
  }));
};

export const updateProductName = async (productId: number, name: string): Promise<void> => {
  await handleResponse(
    await fetch(`/api/products/${productId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    })
  );
};

export const updateProduct = async (
  productId: number,
  payload: {
    base_sku?: string;
    name?: string;
    description?: string | null;
    display_color?: string | null;
    icon_url?: string | null;
    active?: boolean;
    sort_order?: number;
  }
): Promise<{ success: boolean }> => {
  return handleResponse(
    await fetch(`/api/products/${productId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );
};

export const adjustStock = async (
  variantId: number,
  quantity: number,
  reason?: string
): Promise<{ success: boolean; delta: number; newStock: number }> => {
  return handleResponse(
    await fetch('/api/stock/adjust', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantId, quantity, reason })
    })
  );
};

export const deleteProduct = async (
  productId: number
): Promise<{ success: boolean; variantsCount: number; movementsCount: number; alreadyInactive: boolean }> => {
  return handleResponse(
    await fetch(`/api/products/${productId}`, {
      method: 'DELETE'
    })
  );
};

export const deleteVariant = async (
  variantId: number
): Promise<{ success: boolean; movementsCount: number; alreadyInactive: boolean }> => {
  return handleResponse(
    await fetch(`/api/variants/${variantId}`, {
      method: 'DELETE'
    })
  );
};

export const updateVariant = async (
  variantId: number,
  payload: { sku?: string; color?: string | null; size?: string | null; active?: boolean; sort_order?: number }
): Promise<{ success: boolean }> => {
  return handleResponse(
    await fetch(`/api/variants/${variantId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );
};

export const updateProductSortOrder = async (
  productId: number,
  sortOrder: number
): Promise<{ success: boolean }> => {
  return updateProduct(productId, { sort_order: sortOrder });
};

// Removed: getRandomVariant (Mock specific)
export const getRandomVariant = (): string => {
  return "DEMO-ONLY";
};
